import { z } from "zod";

import { UUIDSchema, NonEmptyStringSchema, EmailSchema } from "../common/dto";
import { ShortCrewSchema } from "../crew/dto";

/**
 * User module DTOs
 * Shared types between backend and frontend for user-related endpoints
 */

// ============= Response Schemas =============

/**
 * Base user schema with all core user fields
 */
const BaseUserSchema = z.object({
  id: z.number().int().positive(),
  globalId: UUIDSchema,
  fullname: NonEmptyStringSchema,
  username: NonEmptyStringSchema,
  email: EmailSchema,
  birthday: z.string().nullable().optional(),
  type: z.string(),
  fcl: z.string().nullable().optional(),
  language: z.string(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  status: z.string(),
  payWithInvoice: z.number().nullable().optional(),
  profileimageurl: z.string().nullable().optional(),
  userColor: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
});

/**
 * Schema for a user with their associated crews
 * Used in GET /users and GET /users/crew/:crewId endpoints
 */
export const UserCrewSchema = BaseUserSchema.extend({
  crews: z.array(ShortCrewSchema),
});

/**
 * Schema for a user with their roles in a specific crew
 * Used in GET /crew/:crewId/users endpoint
 */
export const UserWithRolesSchema = BaseUserSchema.extend({
  lang: z.string().optional(),
  roles: z.array(z.string()).optional(),
});

/**
 * Schema for a user with both crews and roles
 * Can be used when full user context is needed
 */
export const UserWithCrewsAndRolesSchema = BaseUserSchema.extend({
  crews: z.array(ShortCrewSchema),
  roles: z.array(z.string()).optional(),
});

export const UserCrewsResponseSchema = z.array(UserCrewSchema);
export const UserWithRolesResponseSchema = z.array(UserWithRolesSchema);

// ============= User Properties Schema =============

export const UserPropertySchema = z.object({
  roleId: z.number().int().positive(),
  positionId: z.number().int().positive(),
  operationId: z.number().int().positive().nullish(),
});

export const UserPropertiesResponseSchema = z.array(UserPropertySchema);

// ============= Current User Profile Schema =============

export const UpdateMyInfoRequestSchema = z.object({
  fcl: z.string().nullable().optional(),
  language: z.enum(["en", "de", "fr", "it"]).optional(),
});

// ============= Type Exports =============

export type UserCrew = z.infer<typeof UserCrewSchema>;
export type UserWithRoles = z.infer<typeof UserWithRolesSchema>;
export type UserWithCrewsAndRoles = z.infer<typeof UserWithCrewsAndRolesSchema>;
export type UserCrewsResponse = z.infer<typeof UserCrewsResponseSchema>;
export type UserWithRolesResponse = z.infer<typeof UserWithRolesResponseSchema>;
export type UserProperty = z.infer<typeof UserPropertySchema>;
export type UserPropertiesResponse = z.infer<typeof UserPropertiesResponseSchema>;
export type UpdateMyInfoRequest = z.infer<typeof UpdateMyInfoRequestSchema>;
